import { I18n } from "@assets/strings";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import styled, { css } from "styled-components";
import { Path, searchParams } from "@core/routing/path";
import { Carousel } from "@domain/carousel/carousel";
import { CountryCode } from "@domain/currentProfile/countryCode";
import { Group } from "@domain/group/group";
import { ContentLicense } from "@domain/licence/contentLicense";
import { Article } from "@domain/lms/article/article";
import { Category } from "@domain/lms/category/category";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { ContentLevel } from "@domain/lms/contentLevel";
import { Course, CourseDuration, CourseProvider } from "@domain/lms/course/course";
import { Quiz } from "@domain/lms/quiz/quiz";
import { Organization } from "@domain/organization/organization";
import { OrganizationSubType, OrganizationType } from "@domain/organization/organizationType";
import { Scoring } from "@domain/ranking/ranking";
import { allSearchDomains, SearchDomain } from "@domain/search/searchDomain";
import { Profile } from "@domain/user/profile";
import { Person } from "@domain/user/user";
import { useIsMobile } from "@utils/hooks";
import { ArticleThumbnailSmall } from "@ui/components/article/articleThumbnailSmall";
import { Secondary } from "@ui/components/containers/textBlocks";
import { CourseThumbnailSmall } from "@ui/components/course/courseThumbnailSmall";
import { DiscussionThumbnailSmall } from "@ui/components/group/discussionThumbnailSmall";
import { ShareroomThumbnailSmall } from "@ui/components/group/shareroomThumbnailSmall";
import { DeleteIconBig, FilterIcon } from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { Page } from "@ui/components/page";
import { CarouselThumbnail } from "@ui/components/pdfCarousel/carouselThumbnail";
import { SuggestedProfileThumbnail } from "@ui/components/profile/suggestedUserThumbnail";
import { QuizThumbnail } from "@ui/components/quiz/quizThumbnail";
import { SearchBar } from "@ui/components/search/searchBar";
import { fadeIn, fromRight } from "@ui/styles/keyframes";
import { theme } from "@ui/styles/theme";
import { gap } from "@ui/styles/utils";
import { Z } from "@ui/styles/zIndex";
import { OrganizationThumbnail } from "./organizationThumbnail";
import { SearchFilters } from "./searchFilters";
import { SearchCarousel, SuggestCarousel } from "./searchPageCarousel";
import { SearchResults } from "./searchResults";

interface CarouselInfo {
	renderer: (item: Person | Course | Carousel | Article | Quiz | Organization | Group) => React.ReactNode;
	suggestKey: string;
	searchKey: string;
	itemHeight?: number;
}

const carouselInfos: { [domain in SearchDomain]: CarouselInfo } = {
	[SearchDomain.Course]: {
		renderer: (course) => <CourseThumbnailSmall key={course.id} course={course as Course} />,
		suggestKey: "search.carousel.suggestion_courses",
		searchKey: "search.carousel.result_courses",
		itemHeight: 255,
	},
	[SearchDomain.Article]: {
		renderer: (article) => <ArticleThumbnailSmall key={article.id} article={article as Article} />,
		suggestKey: "search.carousel.suggestion_articles",
		searchKey: "search.carousel.result_articles",
		itemHeight: 350,
	},
	[SearchDomain.Quiz]: {
		renderer: (quiz) => <QuizThumbnail key={quiz.id} quiz={quiz as Quiz} small />,
		suggestKey: "search.carousel.suggestion_quizzes",
		searchKey: "search.carousel.result_quizzes",
	},
	[SearchDomain.Carousel]: {
		renderer: (carousel) => <CarouselThumbnail key={carousel.id} carousel={carousel as Carousel} />,
		suggestKey: "search.carousel.carousel",
		searchKey: "search.carousel.carousel",
	},
	[SearchDomain.Organization]: {
		renderer: (organization) => (
			<OrganizationThumbnail key={organization.id} organization={organization as Organization} small />
		),
		suggestKey: "search.carousel.suggestion_organizations",
		searchKey: "search.carousel.result_organizations",
	},
	[SearchDomain.User]: {
		renderer: (profile) => <SuggestedProfileThumbnail key={profile.id} profile={profile as Profile} />,
		suggestKey: "search.carousel.suggestion_users",
		searchKey: "search.carousel.result_users",
		itemHeight: 370,
	},
	[SearchDomain.Shareroom]: {
		renderer: (shareroom) => <ShareroomThumbnailSmall key={shareroom.id} shareroom={shareroom as Group} />,
		suggestKey: "search.carousel.suggestion_sharerooms",
		searchKey: "search.carousel.result_sharerooms",
	},
	[SearchDomain.Discussion]: {
		renderer: (discussion) => <DiscussionThumbnailSmall key={discussion.id} discussion={discussion as Group} />,
		suggestKey: "search.carousel.suggestion_discussions",
		searchKey: "search.carousel.result_discussions",
	},
};

interface SearchPageProps extends React.HTMLAttributes<HTMLDivElement> {
	query: string;
	domain?: SearchDomain;
	categories?: Category[];
	levels?: ContentLevel[];
	languages?: ContentLanguage[];
	durations?: CourseDuration[];
	providers?: CourseProvider[];
	orgTypes?: OrganizationType[];
	orgSubTypes?: OrganizationSubType[];
	countries?: CountryCode[];
	license?: ContentLicense[];
	scoring?: Scoring[];
	rating?: number;
}

export const SearchPage: React.FC<SearchPageProps> = ({
	query,
	domain,
	categories,
	levels,
	languages,
	durations,
	providers,
	orgTypes,
	orgSubTypes,
	countries,
	license,
	scoring,
	rating,
	...htmlProps
}) => {
	const searchProps = {
		query,
		domain,
		categories,
		levels,
		languages,
		durations,
		providers,
		orgTypes,
		orgSubTypes,
		countries,
		license,
		scoring,
		rating,
	};
	const [searchValue, setSearchValue] = useState(query);
	const [mobileAutocompleteActive, setMobileAutocompleteActive] = useState(false);
	const [menuState, setMenuState] = useState<"OPEN" | "CLOSED" | "CLOSING">("CLOSED");
	const history = useHistory();
	const isMobile = useIsMobile();
	const [countBySearchCarousel, setCountBySearchCarousel] = useState<{ [key in SearchDomain]?: number }>({});

	const showSuggestions = !query && !categories;
	const noCarouselResult =
		!showSuggestions && allSearchDomains.every((sDomain) => countBySearchCarousel[sDomain] === 0);

	const debouncedChange = useCallback(
		debounce(
			(v) => {
				setSearchValue(v);
				if (isMobile) {
					setMobileAutocompleteActive(true);
				}
			},
			500,
			{ leading: true, trailing: true }
		),
		[]
	);

	useEffect(() => {
		setSearchValue(query);
		setMobileAutocompleteActive(false);
	}, [query]);
	useEffect(() => () => debouncedChange.cancel(), []);

	const closeMenu = (): void => {
		setMenuState("CLOSING");
		setTimeout(() => setMenuState("CLOSED"), 200);
	};

	return (
		<Container
			{...htmlProps}
			header={
				<>
					<SearchBar
						value={searchValue}
						onChange={debouncedChange}
						onSubmit={(value) => {
							history.push({
								pathname: Path.Search,
								search: searchParams(value),
							});
							if (isMobile) {
								setMobileAutocompleteActive(false);
							}
						}}
						placeholder={I18n.t("search.input.placeholder")}
						onFocus={
							isMobile
								? () => {
										setMobileAutocompleteActive(true);
									}
								: undefined
						}
					/>
					<FilterButton onClick={() => setMenuState("OPEN")}>
						<FilterIcon />
					</FilterButton>
				</>
			}
			backText=""
			backPath={Path.Root}
			containerStyle={isMobile ? { backgroundColor: theme.colors.bodyBackground } : undefined}
			backAction={
				mobileAutocompleteActive || query || domain
					? () => {
							history.push({
								pathname: Path.Search,
								search: searchParams(domain ? query : ""),
							});
							setMobileAutocompleteActive(false);
						}
					: undefined
			}
		>
			{/* <HelmetInfos title={I18n.t("seo.search.title")} /> */}
			{domain ? (
				<SearchResultCell {...searchProps} domain={domain} />
			) : (
				<Carousels>
					{noCarouselResult && <Secondary>{I18n.t("search.empty")}</Secondary>}
					{allSearchDomains.map((carouselDomain) => {
						const { renderer, searchKey, suggestKey, itemHeight } = carouselInfos[carouselDomain];
						const commonProps = {
							domain: carouselDomain,
							renderItem: renderer,
						};

						return showSuggestions && carouselDomain !== "CAROUSEL" ? (
							<SuggestCarousel
								key={`${carouselDomain}-suggest`}
								title={I18n.t(suggestKey)}
								itemHeight={itemHeight}
								{...commonProps}
							/>
						) : (
							<SearchCarousel
								key={`${carouselDomain}-search`}
								renderTitle={(count) => I18n.t(searchKey, { count: count ?? 0 })}
								itemHeight={316}
								itemWidth={316}
								onTotalChanged={(count) => setCountBySearchCarousel((c) => ({ ...c, [carouselDomain]: count }))}
								{...searchProps}
								{...commonProps}
							/>
						);
					})}
				</Carousels>
			)}
			{menuState !== "CLOSED" && (
				<Backdrop onClick={closeMenu} closing={menuState === "CLOSING"}>
					<MobileSearchFilters
						query={query}
						domain={domain}
						categories={categories}
						languages={languages}
						durations={durations}
						providers={providers}
						orgTypes={orgTypes}
						orgSubTypes={orgSubTypes}
						levels={levels}
						countries={countries}
						license={license}
						scoring={scoring}
						rating={rating}
					/>
					<CloseButton onClick={closeMenu}>
						<DeleteIconBig />
					</CloseButton>
				</Backdrop>
			)}
			{!isMobile && (
				<DesktopSearchFilters
					query={query}
					domain={domain}
					categories={categories}
					languages={languages}
					durations={durations}
					providers={providers}
					orgTypes={orgTypes}
					orgSubTypes={orgSubTypes}
					levels={levels}
					countries={countries}
					license={license}
					scoring={scoring}
					rating={rating}
				/>
			)}
		</Container>
	);
};

const Container = styled(Page)`
	display: grid;
	grid-template-columns: 280px minmax(0, 1fr);
	grid-template-rows: auto;
	grid-column-gap: 20px;
	grid-row-gap: 24px;
	grid-template-areas:
		"filters main"
		"filters main"
		"filters main"
		". main"
		". main"
		". main";

	@media (max-width: 900px) {
		grid-template-columns: 100%;
		grid-template-areas: "main";
	}
`;

const SearchResultCell = styled(SearchResults)`
	grid-area: main;
`;

const Carousels = styled.div`
	${gap(40)}
	grid-area: main;
`;

const FilterButton = styled.div`
	cursor: pointer;
	${row(0, "center", "center")};
	padding: 10px 0 10px 5px;
`;

const Backdrop = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "closing",
})<{ closing?: boolean }>`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	transition: background-color 0.2s linear;
	background-color: rgba(0, 0, 0, ${({ closing }) => (closing ? 0 : 0.4)});
	z-index: ${Z.above(Z.toolbar)};
	animation: ${fadeIn} 0.2s linear;
	overflow: scroll;

	> * {
		position: absolute;
		top: 0;
		right: 0;
		border-radius: 0;
		justify-content: flex-start;
		animation: ${fromRight} 0.2s ease-in-out;
		transition: transform 0.2s ease-in-out;
		max-width: 80%;
		${({ closing }) =>
			closing &&
			css`
				transform: translateX(100%);
			`}
	}
`;

const CloseButton = styled.div`
	position: absolute;
	${row(0, "center", "center")};
	top: 10px;
	right: 10px;
	width: 22px;
	height: 22px;
	z-index: ${Z.above(Z.modal)};
`;

const DesktopSearchFilters = styled(SearchFilters)`
	grid-area: filters;
	margin-top: 13px;
`;

const MobileSearchFilters = styled(SearchFilters)`
	padding: 20px;
	background-color: ${({ theme }) => theme.colors.white};
`;
